import _isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Dropdown } from '@sravni/react-design-system/lib/Dropdown';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { SelectOption } from '@sravni/react-design-system/lib/Select';
import { Tag } from '@sravni/react-design-system/lib/Tag';
import { ArrowDown, Cross } from '@sravni/react-icons';
import { RegionDialog } from '@sravni/react-region-dialog';
import { useBoolean, usePrevious } from '@sravni/react-utils';
import { ILocation } from '@sravni/types/lib/locations';

import {
  DEFAULT_UNKNOWN_TAG,
  FILTERS,
  FILTERS_NAME,
  PRODUCT_CATEGORIES_BY_PERSON,
  PRODUCT_CATEGORY_LABELS,
} from '@constants/filters';
import { OrganizationType } from '@constants/organizationType';
import { sendFilterEvent } from '@helpers/analyticsEvents';
import { generateReviewsCountOptions } from '@helpers/generateOptions';
import { IRouterQuery } from '@interfaces/routes';
import { useFilters, useOneFilter } from '@store/hooks/filters';
import { useCenters, useNameLocative } from '@store/hooks/locations';
import { useProductsTags } from '@store/hooks/productTags';
import { setOneFilter } from '@store/reducers/filters';
import { fetchRatingsByParams } from '@store/reducers/ratings';
import { updateRouteInBrowser } from '@utils/browserRouting';

import { useOrganizationType } from '../../hooks/organizationType';

import { ProductModal } from './components/ProductModal';
import styles from './filters.module.scss';

export const Filters = React.memo(() => {
  const { query, asPath } = useRouter() as TRouter<IRouterQuery>;
  const centers = useCenters();
  const filters = useFilters();
  const previousFilters = usePrevious<FILTERS>(filters);

  const dispatch = useDispatch();
  const currentCategory = useOneFilter(FILTERS_NAME.PRODUCT_CATEGORY_BY_PERSON) as PRODUCT_CATEGORIES_BY_PERSON;
  const organizationType = useOrganizationType();

  const [isVisibleProductDialog, setVisibleProductDialog] = useBoolean();
  const [isVisibleRegionDialog, setVisibleRegionDialog] = useBoolean();

  const currentTag = filters.reviewTag;
  const allProductTags = useProductsTags()?.all?.options;
  const reviewTagInParams = allProductTags?.find(({ value }) => value === query.reviewTagAlias);

  const nameLocative = useNameLocative(filters.location);
  const href = useRef('');

  useEffect(() => {
    href.current = window.location.href;
  }, [query]);

  const reviewsCountOptions = generateReviewsCountOptions(organizationType) as SelectOption[];
  const reviewsCountOptionLabel = reviewsCountOptions.find(({ value }) => value === filters.userRatingType)?.label;

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (_isEqual(previousFilters, filters)) return;

    updateRouteInBrowser(query, filters, reviewTagInParams);
    dispatch(fetchRatingsByParams(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleChangeUserRatingType = (value) => {
    const filterValue = reviewsCountOptions.find((option) => option.value === value)?.label;
    sendFilterEvent({
      filterName: FILTERS_NAME.USER_RATING_TYPE,
      filterValue,
    });
    dispatch(setOneFilter({ filterName: FILTERS_NAME.USER_RATING_TYPE, value }));
  };

  const handleChangeLocation = ({ name }) => {
    sendFilterEvent({
      filterName: FILTERS_NAME.LOCATION,
      filterValue: name,
    });
  };

  const formatPath = ({ id }: ILocation) => {
    const url = new URL(href.current);
    url.searchParams.set('location', id.toString());

    return url.toString();
  };

  const getReviewTagLabel = () => {
    if (currentTag.key === DEFAULT_UNKNOWN_TAG.key && currentCategory !== PRODUCT_CATEGORIES_BY_PERSON.ALL) {
      return PRODUCT_CATEGORY_LABELS[currentCategory];
    }

    return currentTag.label ?? 'Вид продукта';
  };

  const handleClickLocationFilter = () => {
    if (nameLocative) {
      dispatch(setOneFilter({ filterName: FILTERS_NAME.LOCATION, value: null }));
    } else {
      setVisibleRegionDialog.on();
    }
  };

  return (
    <div className={styles.block}>
      <div className={styles.filters}>
        <Dropdown
          value={filters.userRatingType}
          onChange={handleChangeUserRatingType}
          options={reviewsCountOptions}
          placement="bottom-start"
        >
          <Tag>
            {reviewsCountOptionLabel}
            <Icon icon={<ArrowDown />} />
          </Tag>
        </Dropdown>
        {(organizationType === OrganizationType.BANK ||
          organizationType === OrganizationType.INSURANCE ||
          organizationType === OrganizationType.MFI ||
          organizationType === OrganizationType.COURSES) && (
          <Tag onClick={setVisibleProductDialog.on} size={44}>
            {getReviewTagLabel()} <Icon icon={<ArrowDown />} />
          </Tag>
        )}
        <Tag onClick={handleClickLocationFilter} size={44}>
          {nameLocative ? (
            <>
              {nameLocative} <Icon icon={<Cross />} />
            </>
          ) : (
            <>
              Вся Россия <Icon icon={<ArrowDown />} />
            </>
          )}
        </Tag>
      </div>
      <ProductModal isModalOpened={isVisibleProductDialog} handleClose={setVisibleProductDialog.off} />
      <RegionDialog
        serviceURL={asPath}
        regions={centers}
        visible={isVisibleRegionDialog}
        formatPath={formatPath}
        onClose={setVisibleRegionDialog.off}
        onChange={handleChangeLocation}
      />
    </div>
  );
});
